<script>
    // Licensed under Gnu Public License version 2
    // Copyright (c) 2024-2025 (original work) Open Assessment Technologies SA ;

    import { createEventDispatcher, getContext } from 'svelte';
    import { Icon, IconBarButton } from '@oat-sa-private/ui-elements';
    import FlyoutMenu from '../flyoutMenu/FlyoutMenu.svelte';
    import ExpandButton from './ExpandButton.svelte';
    import { sanitize } from '@oat-sa-private/ui-core';
    import Tooltip from '../tooltip/Tooltip.svelte';

    export let data;
    export let actionable;
    export let actionableLabel;
    export let actionableMenuItems;
    export let nameProperty;
    export let level = 0;

    //in some cases level is needed to define menu entries
    if (data) {
        data.level = level;
    }

    let showFlyout = false;
    let menuItems = [];
    let shouldRenderFlyoutMenu = false;

    let textContainer;
    let tooltipNeeded = false;

    const dispatch = createEventDispatcher();

    $: if (actionable && !data.disabled && !data.disabledBranch && !data.actionsDisabled) {
        if (Array.isArray(actionableMenuItems) && actionableMenuItems.length > 0) {
            menuItems = actionableMenuItems;
            shouldRenderFlyoutMenu = true;
        } else if (typeof actionableMenuItems === 'function') {
            menuItems = actionableMenuItems(data);
            if (menuItems.length) {
                shouldRenderFlyoutMenu = true;
            }
        } else {
            shouldRenderFlyoutMenu = false;
        }
    }

    $: disabled = data?.loading || data?.disabled || data?.disabledBranch;

    //used to track parent width to re-calculate the node label overflow
    let treeDimensions = getContext('treeDimensions');

    $: if ($treeDimensions.width > 0) {
        tooltipNeeded =
            textContainer &&
            (textContainer.scrollWidth > textContainer.clientWidth ||
                textContainer.scrollHeight > textContainer.clientHeight);
    }

    let nodeElement;

    function toggleNode(eventData) {
        dispatch('toggle', eventData);
    }

    function selectNode(eventData) {
        dispatch('select', eventData);
    }

    function handleMenuAction(e) {
        dispatch('menuAction', { action: e.detail.key, data });
    }

    function toggleActionMenu() {
        showFlyout = !showFlyout;
    }

    function closeActionMenu() {
        showFlyout = false;
    }

    function handleSelectionClick(e) {
        e.preventDefault();
        if (!disabled) {
            selectNode(data);
            closeActionMenu();
        }
    }

    function handleToggleClick(e) {
        e.preventDefault();
        toggleNode(data);
        closeActionMenu();
    }

    function handleActionButtonClick(e) {
        e.preventDefault();
        toggleActionMenu();
    }

    function decorateSearch(search, text) {
        text = sanitize(text);

        //add bold highlights
        if (search) {
            const re = new RegExp(search, 'ig');
            return text.replaceAll(re, match => `<b>${match}</b>`);
        } else {
            return text;
        }
    }
</script>

<style>
    @define-mixin positioned-above {
        z-index: var(--layer-1);
    }

    @define-mixin remove-button-style {
        background: none;
        outline: none;
        border: 0;
    }

    li.node {
        --local_action-button-container-margin-left: var(--action-button-container-margin-left, var(--space-2x));

        /* base-16 as default size */
        --local_action-button-placeholder-size: var(--action-button-placeholder-size, 3rem);
        --local_action-button-placeholder-width: var(
            --action-button-placeholder-width,
            var(--local_action-button-placeholder-size)
        );
        --local_action-button-placeholder-height: var(
            --action-button-placeholder-height,
            var(--local_action-button-placeholder-size)
        );

        display: flex;
        flex: 0 0 5.5rem;
        align-items: stretch;
        justify-content: flex-start;

        & .selected-indicator {
            --local_level: var(--level, 0);

            @add-mixin positioned-above;

            flex: 0 0 0.5rem;
            margin-right: calc(3rem * var(--local_level));
        }

        & .name-container {
            @add-mixin positioned-above;
            @add-mixin remove-button-style;

            padding: 1.25rem 0 0.75rem 0;
            line-height: 3rem;
            text-align: left;
            font-size: var(--fontsize-body);
            display: flex;
            flex-grow: 1;
            overflow: hidden;

            & div {
                display: -webkit-box;
                -webkit-line-clamp: var(--maxLinesPerNode, 2);
                -webkit-box-orient: vertical;
                overflow: hidden;
                word-break: break-all;
                text-overflow: ellipsis;
            }

            &.action-placeholder:after {
                content: '';

                @add-mixin positioned-above;

                flex: 0 0 5rem;

                margin-left: var(--local_action-button-container-margin-left);
                width: var(--local_action-button-placeholder-width);
                height: var(--local_action-button-placeholder-height);
            }
        }

        & .action-button-container,
        .actions-disabled-container {
            @add-mixin positioned-above;
            display: flex;
            align-items: flex-start;
            justify-content: center;

            flex: 0 0 5rem;
            margin-left: var(--local_action-button-container-margin-left);

            & .disabled-icon-container {
                padding-top: 1.5rem;
                display: flex;
                justify-content: center;
            }
        }

        & .node-container {
            @add-mixin remove-button-style;

            display: flex;
            position: relative;
            align-items: stretch;
            justify-content: flex-start;
            padding: 0;
            width: 100%;

            &.disabled {
                color: var(--color-gs-disabled);
                --dot-icon-border-color: var(--color-gs-disabled);
            }

            &.selected {
                background-color: var(--color-node-selected);

                & .selected-indicator {
                    background-color: var(--color-node-selected-indicator);
                }

                & .name-container {
                    /* to avoid layout shift */
                    transition: text-shadow 0.1s;
                    text-shadow: 0 0 0.65px currentColor, 0 0 0.65px currentColor;
                }
            }

            &:hover {
                background-color: var(--color-node-hover);
            }
        }

        & .focus-hook {
            @add-mixin remove-button-style;
            @add-mixin outline-focus 0;

            position: absolute;
            z-index: var(--layer-0);
            width: 100%;
            height: 100%;

            &:focus-visible {
                background-color: var(--color-node-hover);
            }
        }
    }

    :global(li.node .action-button-container.active button) {
        border: var(--border-thin) solid var(--color-border-default);
    }

    :global(li.node .expand-button-container) {
        @add-mixin positioned-above;
    }
</style>

{#if data && !data.hidden}
    <li class="node">
        <div class="node-container" class:selected={data.selected} class:disabled>
            <button class="focus-hook" on:click={handleSelectionClick} aria-label={data[nameProperty]} />
            <div class="selected-indicator" style={`--level: ${level};`} />
            <ExpandButton
                expanded={data.expanded}
                expandable={(data.children && data.children.length) || data.totalChildren > 0}
                disabled={data.disabledBranch}
                loading={data.loading}
                on:click={handleToggleClick} />
            <button
                class="name-container"
                class:action-placeholder={shouldRenderFlyoutMenu && !data.selected}
                tabindex="-1"
                aria-hidden="true"
                {disabled}
                on:click={handleSelectionClick}>
                <div bind:this={textContainer}>
                    {@html decorateSearch(data.search, data[nameProperty])}
                </div>
            </button>
            {#if data.actionsDisabled}
                <div class="actions-disabled-container">
                    <div class="disabled-icon-container">
                        <Icon name="disable-thin-16" />
                    </div>
                </div>
            {:else if data.selected && shouldRenderFlyoutMenu}
                <div class="action-button-container" class:active={showFlyout} bind:this={nodeElement}>
                    <IconBarButton
                        icon="more-16"
                        size="base-16"
                        label={actionableLabel}
                        shape="circular"
                        on:click={handleActionButtonClick} />
                </div>
            {/if}
        </div>
        {#if data.selected && shouldRenderFlyoutMenu}
            <FlyoutMenu
                bind:open={showFlyout}
                items={menuItems}
                caller={nodeElement}
                on:action={handleMenuAction}
                anchoring="top right" />
        {/if}
    </li>
    {#if data.children && data.expanded && !data.disabledBranch}
        {#each data.children as child}
            <svelte:self
                data={child}
                level={level + 1}
                {nameProperty}
                {actionable}
                {actionableLabel}
                {actionableMenuItems}
                on:toggle
                on:select
                on:menuAction />
        {/each}
    {/if}
    {#if tooltipNeeded}
        <Tooltip anchorElement={textContainer} text={data[nameProperty]} />
    {/if}
{/if}
